import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import './../ChangePassword.css';
import { WebSocketContext } from "../WebSocketContext";
import { apiAppFetch } from '../utils/apiFetch';
import { storeToken } from '../utils/auth';

const Icon = ({ name, className = "" }) => {
  const paths = {
    eye: "M12 5c-7 0-10 7-10 7s3 7 10 7 10-7 10-7-3-7-10-7zm0 11a4 4 0 1 1 0-8 4 4 0 0 1 0 8z",
    eyeOff: "M2 4l2.2 2.2C2.9 7.5 2 9 2 9s3 7 10 7c2 0 3.7-.6 5.1-1.5L20 17l2-2-18-11zm10 10c-3.1 0-5.4-2.7-6.6-4.4.5-.8 1.3-1.9 2.5-2.9l1.6 1.0A4 4 0 0 0 12 14zm0-8c.4 0 .8.1 1.2.2l2.2 1.4c.3.4.5.9.6 1.4.0.1 0 .3 0 .4 0 2.2-1.8 4-4 4-.4 0-.7-.1-1.1-.2l-2.1-1.3c-.5-.7-.8-1.5-.8-2.5 0-2.2 1.8-4 4-4z",
    back: "M15.41 7.41 14 6l-6 6 6 6 1.41-1.41L10.83 12z"
  };
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={`icon ${className}`} fill="currentColor">
      <path d={paths[name]} />
    </svg>
  );
};

const ChangePassword = () => {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [logoutAllDevices] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [touched, setTouched] = useState({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false
  });
  const { userDetails } = useContext(WebSocketContext);
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const initSetupDone = userDetails?.initSetupDone ?? localStorage.getItem('initSetupDone');
  const isInitialSetup = String(initSetupDone) === '0';

  useEffect(() => {
    if (isInitialSetup) {
      const handlePopState = () => {
        window.history.pushState(null, '', location.pathname);
      };

      window.history.pushState(null, '', location.pathname);
      window.addEventListener('popstate', handlePopState);

      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, [isInitialSetup, location.pathname]);

  const validateForm = () => {
    if (!oldPassword.trim()) {
      setError('Current password is required.');
      return false;
    }
    if (!newPassword.trim()) {
      setError('New password is required.');
      return false;
    }
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return false;
    }
    if (newPassword !== confirmPassword) {
      setError('New password and confirm password do not match.');
      return false;
    }
    if (oldPassword === newPassword) {
      setError('New password must be different from current password.');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ oldPassword: true, newPassword: true, confirmPassword: true });
    setError('');
    setSuccess('');

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    const data = {
      oldPass: oldPassword,
      newPass: newPassword,
      cPass: confirmPassword,
      logoutAllDevices: logoutAllDevices ? 1 : 0,
    };

    try {
      const response = await apiAppFetch('changePassword', {
        method: 'POST',
        body: JSON.stringify(data),
      });

      const res_data = await response.json();
      if (res_data.success) {
        if (res_data.token) {
          await storeToken({
            token: res_data.token,
            clientId: localStorage.getItem('userId'),
            initSetupDone: res_data.initSetupDone,
          });
        }

        if (res_data.initSetupDone !== undefined) {
          localStorage.setItem('initSetupDone', res_data.initSetupDone);
        } else {
          localStorage.setItem('initSetupDone', "1");
        }

        setSuccess(res_data.message || 'Password changed successfully!');
        setTimeout(() => {
          navigate('/');
        }, 1500);
      } else {
        setError(res_data.message || 'Failed to change password. Please try again.');
      }
    } catch (err) {
      setError('Error in updating password. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const oldPasswordError = touched.oldPassword && !oldPassword.trim() ? 'Current password is required.' : '';
  const newPasswordError = touched.newPassword && !newPassword.trim()
    ? 'New password is required.'
    : touched.newPassword && newPassword.length > 0 && newPassword.length < 8
    ? 'Password must be at least 8 characters.'
    : '';
  const confirmPasswordError = touched.confirmPassword && !confirmPassword.trim()
    ? 'Re-enter new password is required.'
    : touched.confirmPassword && confirmPassword !== newPassword && confirmPassword.length > 0
    ? 'Passwords do not match.'
    : '';

  const canSubmit = !oldPasswordError && !newPasswordError && !confirmPasswordError &&
    oldPassword.trim() && newPassword.trim() && confirmPassword.trim() &&
    newPassword.length >= 8 && newPassword === confirmPassword && !isLoading;

  return (
    <div className={`change-password-container ${isDarkMode ? 'dark-theme' : 'light-theme'}`} data-theme={isDarkMode ? 'dark' : 'light'}>
      <div className="change-password-page">
        <header className="change-password-topbar">
          {!isInitialSetup && (
            <button
              type="button"
              className="change-password-back"
              onClick={() => navigate(-1)}
              aria-label="Go back"
            >
              <Icon name="back" />
            </button>
          )}
          <h1 className="change-password-title">Change Password</h1>
        </header>

        <form onSubmit={handleSubmit} className="change-password-form" noValidate>
          {success && (
            <div className="success-alert" role="alert">
              {success}
            </div>
          )}

          {error && (
            <div className="error-alert" role="alert">
              {error}
            </div>
          )}

          <div className="form-field">
            <label className="field-label">Current Password</label>
            <div className="input-wrapper">
              <input
                type={showOldPassword ? 'text' : 'password'}
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                onBlur={() => setTouched(t => ({ ...t, oldPassword: true }))}
                placeholder="Enter your Current Password"
                autoComplete="current-password"
                className={`change-password-input ${oldPasswordError ? 'input-error' : ''}`}
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowOldPassword(s => !s)}
                className="password-toggle"
                aria-label={showOldPassword ? 'Hide password' : 'Show password'}
                disabled={isLoading}
              >
                <Icon name={showOldPassword ? 'eyeOff' : 'eye'} />
              </button>
            </div>
            {oldPasswordError && <p className="field-error">{oldPasswordError}</p>}
          </div>

          <div className="form-field">
            <label className="field-label">New Password</label>
            <div className="input-wrapper">
              <input
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                onBlur={() => setTouched(t => ({ ...t, newPassword: true }))}
                placeholder="Enter your New Password"
                autoComplete="new-password"
                className={`change-password-input ${newPasswordError ? 'input-error' : ''}`}
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(s => !s)}
                className="password-toggle"
                aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                disabled={isLoading}
              >
                <Icon name={showNewPassword ? 'eyeOff' : 'eye'} />
              </button>
            </div>
            {newPasswordError && <p className="field-error">{newPasswordError}</p>}
          </div>

          <div className="form-field">
            <label className="field-label">Re-enter New Password</label>
            <div className="input-wrapper">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onBlur={() => setTouched(t => ({ ...t, confirmPassword: true }))}
                placeholder="Re-enter your New Password"
                autoComplete="new-password"
                className={`change-password-input ${confirmPasswordError ? 'input-error' : ''}`}
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(s => !s)}
                className="password-toggle"
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                disabled={isLoading}
              >
                <Icon name={showConfirmPassword ? 'eyeOff' : 'eye'} />
              </button>
            </div>
            {confirmPasswordError && <p className="field-error">{confirmPasswordError}</p>}
          </div>

          <label className="logout-devices-option is-locked">
            <input
              type="checkbox"
              checked={logoutAllDevices}
              readOnly
              disabled
            />
            <span className="logout-devices-box" aria-hidden="true" />
            <span className="logout-devices-label">Logout from all devices</span>
          </label>

          <button
            type="submit"
            disabled={!canSubmit}
            className={`change-password-button ${canSubmit ? 'button-enabled' : 'button-disabled'}`}
          >
            {isLoading ? (
              <span className="button-loading">
                <span className="spinner"></span>
                Updating password…
              </span>
            ) : (
              'Change Password'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChangePassword;
